import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl, getLocale } from 'umi';

import { useSelectArrObj } from '@/pages/dashboard/utils/customHook';
import { addLastDateRequestValue, formatFilterValues, formatSingleTime, normalizeFormatRules } from '@/pages/dashboard/utils/utils';
import { getDefaultUtcDateValue } from '@/pages/dashboard/utils/dateUtils';
import ChartItem from './chartItem';
import { EngagementRevenueMetricsProps } from '../../_config/interface';
import { getApiData } from '@/pages/dashboard/services/common';
import { PERIODTIMEOBJ } from '@/pages/dashboard/_config/constant';
import moment from 'moment';
import { formatDataByRules, IFormatRule } from '@/pages/dashboard/utils/dataFormatter';
import { getCustomFilterList } from '@/pages/dashboard/components/NewFilter/customFilterList';
import { IApiResponseData, IMetricsEntry } from '@/pages/dashboard/pages/overview/components/OverviewMetricsChart/type';
import {
  applyEventTypesDisplayLastToOverviewEventData,
  processApiData,
} from '@/pages/dashboard/pages/overview/components/OverviewMetricsChart/utils';
import { stringToFunction } from '@/pages/dashboard/utils/stringToJson';

export default function OverviewMetricsChart(props: EngagementRevenueMetricsProps) {
  const {
    gameCode,
    filterLoading,
    renderIndex,
    formValue, // 本来只取时间就行，这里防止后面产品要求传递其他筛选器参数，所以直接把所有参数都传递下来
    placeholderId = '',
    type = 'DAY',
    eventData = [],
    tabConfig = {}, // 卡片配置
    intList = [],
    percentList = [],
    excelFilterName = '',
    api,
    loading,
    isMoblie = false,
    config,
    timeZone = 0,
    showLastMonthYear = false,
    formatRules,
    visibleLegends,
    viewportFullscreen,
    /** 由外层（OverviewMetricsTrendCo）传入：注入到 chartItem 累计值下拉右侧的额外 slot */
    leftExtraSlot,
    /** 由外层传入：叠加在 chart 区域内部的 overlay（如全屏 EventWindows 浮窗）*/
    chartOverlaySlot,
    showSegmentTabsInFullScreen = false,
    eventFilterBesideTitle = false,
    eventTypeMaxCount,
  } = props;
  const {
    haveLastDate,
    defaultSegment,
    defaultDimension,
    extraMetrics,
    dimensionOptions,
    metricLookbackDaysMap,
    eventTypesDisplayLast,
  } = config || {};
  const { formatMessage } = useIntl(); // 中英切换方法

  // loading：若 snapshot 中已有当前 renderIndex 对应接口的旧数据，则 remount 后跳过初始 loading，避免「关闭全屏后重新加载」的视觉闪烁
  const [updateLoading, setLoading] = useState(() => {
    const ownSnapshotKey = `overviewMetricsData_${renderIndex}`;
    const hasOwnSnapshot = Boolean(
      viewportFullscreen?.getChildSortSnapshot?.(ownSnapshotKey),
    );
    return !hasOwnSnapshot;
  });
  const [error, setError] = useState(false);
  // const [noData, setNodata] = useState(false);
  const [legends, setLegends] = useState<string[] | undefined>(undefined);


  // 标准化 formatRules,将 keys 数组转换为单个 key 的规则数组
  const normalizedFormatRules: IFormatRule[] = useMemo(() => {
    return normalizeFormatRules(formatRules);
  }, [formatRules]);

  // 获取默认 tab：如果 config.defaultSegment 存在，优先使用它；否则使用第一个
  const getDefaultTab = (tabs) => {
    if (!tabs || tabs.length === 0) return { value: '', text: '' };
    if (defaultSegment) {
      const targetTab = tabs.find((tab) => tab.value === defaultSegment);
      if (targetTab) {
        return { value: targetTab.value, text: targetTab.text };
      }
    }
    return { value: tabs[0]?.value, text: tabs[0]?.text };
  };

  const defaultTabInfo = getDefaultTab(tabConfig);

  // 配置项
  // 用 viewportFullscreen.getChildSortSnapshot 取出上次保存的 defaultData，
  // 避免 portal 容器切换导致组件 remount 后接口数据丢失、必发一次新请求造成闪烁
  const persistKeyData0 = `overviewMetricsData_0`;
  const persistKeyData1 = `overviewMetricsData_1`;
  const [options, setOptions] = useState(() => {
    const snap0 = viewportFullscreen?.getChildSortSnapshot?.(persistKeyData0);
    const snap1 = viewportFullscreen?.getChildSortSnapshot?.(persistKeyData1);
    return [
      {
        api: '',
        title: formatMessage({ id: 'overview_Engagement_Metrics_Trends' }),
        tabList: tabConfig,
        defaultData: snap0 ?? null,
        defaultTab: defaultTabInfo.value,
        defaultTabText: defaultTabInfo.text,
        excelFilterName: excelFilterName ? excelFilterName : formatMessage({ id: 'overview_Engagement_Metrics_Trends' }),
      },
      {
        api: '',
        title: formatMessage({ id: 'overview_Revenue_Metrics_Trends' }),
        tabList: tabConfig,
        defaultData: snap1 ?? null,
        defaultTab: defaultTabInfo.value,
        defaultTabText: defaultTabInfo.text,
        // 在线时长指标
        excelFilterName: excelFilterName ? excelFilterName : formatMessage({ id: 'overview_Revenue_Metrics_Trends' }),
      },
    ];
  });
  const optionsRef = useRef(options);
  const requestSeqRef = useRef(0);
  const unmountedRef = useRef(false);
  // 当前卡片value
  // 用 viewportFullscreen 的 snapshot ref 兜底：ViewportFullscreenShell 切换全屏时会改 createPortal 容器，
  // 导致 portal 子树 unmount/remount，组件本地 useState 全部丢失。Shell 自身不卸载，其 ref 跨切换稳定，
  // 所以把 active/selected 写到 Shell 的 snapshot ref 中，重挂载时用作 useState 初值。
  const persistKeyActive = `overviewMetricsActive_${renderIndex}`;
  const persistKeySelected = `overviewMetricsSelected_${renderIndex}`;
  const persistKeyAggregate = `overviewMetricsAggregate_${renderIndex}`;
  const readAggregateSnapshot = (): IMetricsEntry[] | undefined => {
    const snap = viewportFullscreen?.getChildSortSnapshot?.(persistKeyAggregate);
    return Array.isArray(snap) ? (snap as IMetricsEntry[]) : undefined;
  };
  const [aggregateData, setAggregateDataState] = useState<IMetricsEntry[] | undefined>(
    readAggregateSnapshot,
  );
  const setAggregateData = (aggregate: IMetricsEntry[] | undefined): void => {
    viewportFullscreen?.setChildSortSnapshot?.(persistKeyAggregate, aggregate);
    setAggregateDataState(aggregate);
  };
  const [active, setActive] = useState(() => {
    const snap = viewportFullscreen?.getChildSortSnapshot?.(persistKeyActive);
    return typeof snap === 'string' && snap ? snap : defaultTabInfo.value;
  });
  const [selected, setSelected] = useState(() => {
    const snap = viewportFullscreen?.getChildSortSnapshot?.(persistKeySelected);
    return typeof snap === 'string' && snap ? snap : (defaultDimension || 'accumulated');
  });
  const [regionDataType, setRegionDataType] = useState('');
  // 更新tab value
  const updateTabAndSelected = (tab?: string, selectedNext?: string) => {
    if (tab !== undefined) {
      viewportFullscreen?.setChildSortSnapshot?.(persistKeyActive, tab);
    }
    if (selectedNext !== undefined) {
      viewportFullscreen?.setChildSortSnapshot?.(persistKeySelected, selectedNext);
    }
    setActive(tab);
    setSelected(selectedNext);
  };

  // 更新 tab 对应的 data
  const updateData = (targetRenderIndex: number, res: any) => {
    // 同步写 snapshot：portal 容器切换导致 remount 时，能从这里读回最近一次接口数据
    const snapKey = targetRenderIndex === 0 ? persistKeyData0 : persistKeyData1;
    viewportFullscreen?.setChildSortSnapshot?.(snapKey, res);
    setOptions((prev: any[]) => prev.map((option: any, index: number) => {
      if (index !== targetRenderIndex) {
        return option;
      }
      return {
        ...option,
        defaultData: res,
      };
    }));
  };

  const separatedOption = useSelectArrObj(formValue, undefined, undefined, undefined, undefined, undefined, undefined, dimensionOptions, {
    currentSegment: active,
  });

  // 获取separatedOption
  const getAccumulated = (selected: string, customAccumulationKeys?: string[]) => {
    const accumulation: any = {};
    
    // 遍历所有标准维度选项
    separatedOption?.forEach((item: any) => {
      const dimensionValue = item?.value;
      
      // 如果该维度在 customAccumulationKeys 中,跳过生成标准 by_xxx 字段
      if (customAccumulationKeys?.includes(dimensionValue)) {
        return;
      }
      
      // 生成标准维度字段
      accumulation[`by_${dimensionValue}`] = (dimensionValue === selected && dimensionValue !== 'accumulated');
    });
    
    // 处理自定义累加维度
    // 只有当 selected 在 customAccumulationKeys 中时,才添加到 custom_accumulation
    if (customAccumulationKeys && customAccumulationKeys.length > 0 && customAccumulationKeys.includes(selected)) {
      accumulation.custom_accumulation = [
        {
          key: selected,
          is_accumulation: true
        }
      ];
    }
    
    return accumulation;
  };

  // pc_demo的Avg.Session Length过小，与sally姐核对后，其他业务几乎不会那么小所以统一保留两位小数
  const formatAvgSessionTime = (res: any[]) =>{
    const data = res?.Nodes ? res?.Nodes : res;
    return data?.map((item: any) => {
      if (item.metrics?.avg_session_time) {
        return {
          ...item,
          metrics: {
            ...item.metrics,
            avg_session_time: {
              ...item.metrics.avg_session_time,
              value: parseFloat(item.metrics.avg_session_time.value).toFixed(2),
              last_value: parseFloat(item.metrics.avg_session_time.last_value).toFixed(2),
            },
          },
        };
      }
      return item;
    });
  };

  // 应用 formatRules 格式化数据
  const applyFormatRules = (data) => {
    if (!normalizedFormatRules || normalizedFormatRules.length === 0) {
      return data;
    }

    try {
      // 深拷贝数据以避免修改原始数据
      const clonedData = JSON.parse(JSON.stringify(data));
      
      // 应用格式化规则
      formatDataByRules(clonedData, normalizedFormatRules);
      
      return clonedData;
    } catch (error) {
      console.error('applyFormatRules 错误:', error);
      return data;
    }
  };


  const {
    metricsApi = '',
    dimensionApi = '',
    beforeMetricsApiRequest = (d: unknown) => d,
    beforeDimensionApiRequest = (d: unknown) => d,
    formatMetricsApiData = (d: unknown) => d,
    formatDimensionApiData = (d: unknown) => d,
    extraParams
  } = api || {};
  // 更新数据
  const update = async (tab: string | undefined, selectedParam: string | undefined) => {
    const currentRequestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = currentRequestSeq;

    let selected = selectedParam;
    if (tab && selected && selected !== 'accumulated' && Array.isArray(dimensionOptions)) {
      const optionConf = dimensionOptions.find((d) => d?.value === selected);
      if (optionConf && typeof optionConf !== 'string' && optionConf.excludedMetrics?.includes(tab)) {
        selected = 'accumulated';
      }
    }

    updateTabAndSelected(tab, selected);
    const metrics = tab;
    // 非继承，第一次进入页面折线图区14天、6周、6月
    const { rangeTime, timeType, diff, ...filters } = formValue;
    const defaultTimeRange = getDefaultUtcDateValue(type, rangeTime);
    const rangeTimeArr = diff ? defaultTimeRange : formatSingleTime(rangeTime, timeType);
    // PUBG wow玩法指标 默认起始日期特殊处理 wow_player_d30 和wow_author_d30
    const [start, end] = rangeTimeArr;
    let startTime = start;
    if (diff && (metrics === 'wow_player_d30' || metrics === 'wow_author_d30')) {
      startTime = moment(start).subtract(46, 'days').format('YYYY-MM-DD');
    }
    const timeTypeKey = String(timeType).toLowerCase();
    const lookbackConfig = metrics && metricLookbackDaysMap ? metricLookbackDaysMap[metrics] : undefined;
    const metricLookbackDays = lookbackConfig?.[timeTypeKey];
    if (diff && metricLookbackDays) {
      startTime = moment(end).subtract(metricLookbackDays - 1, 'days').format('YYYY-MM-DD');
    }
    let params: any = {
      game_code: gameCode,
      filters: formatFilterValues(filters),
      start_time: startTime,
      end_time: end,
      period: PERIODTIMEOBJ?.[timeType],
      ...extraParams,
    };

    const isCurrentRequest = () => !unmountedRef.current && requestSeqRef.current === currentRequestSeq;

    params = addLastDateRequestValue(params, haveLastDate, timeZone);
    setLoading(true);
    setError(false);
    setAggregateData(undefined);
    setLegends(undefined);
    // 切换tab和切换下拉选请求
    if (selected === 'accumulated') {
      params.metrics = [metrics] ?? [];
      getApiData(metricsApi, stringToFunction(beforeMetricsApiRequest as any)(params)).then((apiData: IApiResponseData | IMetricsEntry[]) => {
        if (!isCurrentRequest()) {
          return;
        }
        const { list, aggregate, dimInfo } = processApiData(apiData);
        if (aggregate) setAggregateData(aggregate);
        if (dimInfo) setLegends(dimInfo);

        // 应用格式化规则
        const formattedData = applyFormatRules(list);
        const formatData = formatAvgSessionTime(formattedData);
        const res = (formattedData)?.Nodes ? {
          Nodes: formatData,
        } : formatData;
        // mobile框架接口数据差异化处理
        if (isMoblie) {
          const mobileRes = res?.Nodes?.map((item: any) => {
            const { date, ...rest } = item;
            return { date, metrics: rest };
          });
          updateData(renderIndex, mobileRes);
        } else {
          updateData(renderIndex, res);
        }
        setLoading(false);
        setError(false);
      }).catch((e: any) => {
        if (!isCurrentRequest()) {
          return;
        }
        setAggregateData(undefined);
        setLegends(undefined);
        setLoading(false);
        setError(e);
      });
    } else {
      let metric = tab ? tab : '';
      if (isMoblie) {
        // 根据tab映射字段名
        const keyMap: any = {
          // daily
          'dau': 'active_users',
          // weekly
          'wau': 'active_users',
          'avg.wau': 'avg_active_users',
          // monthly
          'mau': 'active_users',
          'avg.mau': 'avg_active_users',
          // Engagement Metrics Trends
          'new': 'new_users',
          'tru': 'total_users',
          // Revenue Metrics Trends
          'revenue': 'pay_amount',
          'payer': 'paid_users',
          'payer_ratio': 'payer_rate',
        };
        metric = tab ? (keyMap[tab] ?? tab) : '';
      }
      const accumulation = getAccumulated(selected ? selected : 'accumulated', ["campaign", "network_type", "ua_network", "user_type", "version", "ad_format"]);
      // 分维度参数
      const dimensionParams = { ...params, metric, accumulation };
      getApiData(dimensionApi, stringToFunction(beforeDimensionApiRequest as any)(dimensionParams)).then((apiData: IApiResponseData | IMetricsEntry[]) => {
        if (!isCurrentRequest()) {
          return;
        }
        const { list, aggregate, dimInfo } = processApiData(apiData);
        if (aggregate) setAggregateData(aggregate);
        if (dimInfo) setLegends(dimInfo);

        // 应用格式化规则
        const formattedData = applyFormatRules(list);
        const res = formatAvgSessionTime(formattedData);
        // 处理数据 tab字段名映射
        res?.map((item) => {
          tab && (item.metrics[tab] = item?.metrics[metric]);
        });
        updateData(renderIndex, res);
        // setNodata(res?.length === 0 || !res)
        setLoading(false);
        setError(false);
      }).catch((e: any) => {
        if (!isCurrentRequest()) {
          return;
        }
        setAggregateData(undefined);
        setLegends(undefined);
        setLoading(false);
        setError(e);
      });
    }
    if (!isMoblie) {
      if (selected === 'system') {
        setRegionDataType('os');
      } else {
        setRegionDataType('');
      }
    }
  };

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      requestSeqRef.current += 1;
    };
  }, []);

  useEffect(() => {
    if (type && type !== 'day') {
      // week、month的tabList配置， 默认day
      // 获取默认 tab：如果 config.defaultSegment 存在，优先使用它；否则使用第一个
      let defaultTabValue = tabConfig?.[0]?.value || '';
      let defaultTabText = tabConfig?.[0]?.text || '';
      if (defaultSegment && tabConfig) {
        const targetTab = tabConfig.find((tab) => tab.value === defaultSegment);
        if (targetTab) {
          defaultTabValue = targetTab.value;
          defaultTabText = targetTab.text;
        }
      }
      setOptions((prev: any[]) => {
        const next = [...prev];
        if (next[0]) {
          next[0] = {
            ...next[0],
            tabList: tabConfig,
            defaultTab: defaultTabValue,
            defaultTabText: defaultTabText,
          };
        }
        if (next[1]) {
          next[1] = {
            ...next[1],
            tabList: tabConfig,
            defaultTab: defaultTabValue,
            defaultTabText: defaultTabText,
          };
        }
        return next;
      });

      // 只在当前 active 不在新的 tabConfig 中时才重置
      const isActiveInTabConfig = tabConfig.some((tab: { value: string; }) => tab.value === active);
      if (!isActiveInTabConfig) {
        updateTabAndSelected(defaultTabValue, 'accumulated');
      }
    }
  }, [type, defaultSegment, tabConfig, active]);

  useEffect(() => {
    const options = optionsRef?.current;
    setOptions([
      {
        ...options[0],
        title: formatMessage({ id: 'overview_Engagement_Metrics_Trends' }),
        tabList: tabConfig,
        excelFilterName: excelFilterName ? excelFilterName : formatMessage({ id: 'overview_Engagement_Metrics_Trends' }),
      },
      {
        ...options[1],
        title: formatMessage({ id: 'overview_Revenue_Metrics_Trends' }),
        tabList: tabConfig,
        excelFilterName: excelFilterName ? excelFilterName : formatMessage({ id: 'overview_Revenue_Metrics_Trends' }),
      },
    ]);
  }, [getLocale()]);

  // 标记是否为「portal 重挂载后的首次 effect 跑」：若当前 renderIndex 对应的 snapshot 已有数据，则首次跳过请求，避免闪烁
  const skipFirstFetchRef = useRef(
    Boolean(
      viewportFullscreen?.getChildSortSnapshot?.(
        renderIndex === 0 ? persistKeyData0 : persistKeyData1,
      ),
    ),
  );

  useEffect(() => {
    if (filterLoading) return;
    if (skipFirstFetchRef.current) {
      // 用 snapshot 数据填充 UI，等下次 formValue / 用户交互再发请求
      skipFirstFetchRef.current = false;
      setLoading(false);
      return;
    }
    update(active, selected);
  }, [formValue, filterLoading, renderIndex]);

  const { tabList, defaultTab, defaultTabText, defaultData, excelFilterName: excelFileName } = options[renderIndex];
  // 用 active 覆盖 defaultTab：portal 容器切换会让 ChartItem 重挂载，
  // useItem 的 useState(defaultTab) 必须读到当前真正选中的 tab，否则会回到 DAU 等初始值
  const effectiveDefaultTab = active || defaultTab;
  const effectiveDefaultTabText =
    tabList?.find?.((tab: { value: string; }) => tab.value === effectiveDefaultTab)?.text || defaultTabText;

  const displayEventData = useMemo(
    () =>
      applyEventTypesDisplayLastToOverviewEventData(
        eventData,
        eventTypesDisplayLast,
      ),
    [eventData, eventTypesDisplayLast],
  );

  const chartLoading = Boolean(loading || updateLoading);

  return (
    <>
      <ChartItem
        config={config}
        isMoblie={isMoblie}
        tabConfig={tabConfig}
        intList={intList}
        percentList={percentList}
        placeholderId={placeholderId}
        gameCode={gameCode}
        eventData={displayEventData}
        selected={selected}
        renderIndex={renderIndex}
        loading={chartLoading}
        filterLoading={filterLoading}
        error={error}
        update={update}
        excelFilterName={excelFileName}
        defaultTab={effectiveDefaultTab}
        defaultTabText={effectiveDefaultTabText}
        defaultData={defaultData}
        aggregateData={aggregateData}
        tabList={tabList}
        type={type}
        formValue={formValue}
        separatedOption={separatedOption}
        regionDataType={regionDataType}
        showLastMonthYear={showLastMonthYear}
        visibleLegends={visibleLegends}
        extraMetrics={extraMetrics}
        legends={legends}
        viewportFullscreen={viewportFullscreen}
        leftExtraSlot={leftExtraSlot}
        chartOverlaySlot={chartOverlaySlot}
        showSegmentTabsInFullScreen={showSegmentTabsInFullScreen}
        eventFilterBesideTitle={eventFilterBesideTitle}
        eventTypeMaxCount={eventTypeMaxCount}
      />
    </>
  );
}
